import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../hooks/useUrlParam';
import { arrayMove } from '@dnd-kit/sortable';
import { useSearchParams } from 'react-router-dom';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../../../lib/supabase';
import { uniqueChannelName } from '../../../lib/realtime';
import type { Client } from '../../../context/ClientContext';
import { useUser } from '../../../context/UserContext';
import { CONTENT_QUEUE_STATUSES, DEFAULT_CONTENT_QUEUE_STATUS, contentQueueStatus, type ContentQueueStatus } from '../../../lib/contentQueue';
import type { AiJob } from '../../../lib/aiJobs';
import { AI_JOB_STATUS_LABEL, aiJobChipLabel, aiJobStatusTone } from '../../../lib/aiJobLabels';
import {
  approveRow,
  briefOf,
  CONTENT_TYPES,
  contentTypeOf,
  enqueueSweepJob,
  fetchBuildJobs,
  fetchQueue,
  fetchSweepState,
  goesToBuildQueue,
  nextPosition,
  persistOrder,
  sendToBuildQueue,
  sendToWriter,
  tierOf,
  TIERS,
  type ContentQueueRow,
  type ContentType,
  type SweepState,
  type Tier,
} from '../../../lib/keywordSweep';
import AiJobChips from '../../ai/AiJobChips';
import { Badge, Button, Card, Dropdown, Input, KebabMenu, Popover, SidePanel, TableColumn, useToast } from '../../ui';
import DenyPanel from '../../contentQueue/DenyPanel';
import QueueFilters, { type TierFilter, type TypeFilter } from '../../contentQueue/QueueFilters';
import QueueTable from '../../contentQueue/QueueTable';
import { fetchOffers, isMoneyPage, type Offer } from '../../../lib/offers';
import SweepSettingsPanel from '../../contentQueue/SweepSettingsPanel';
import SweepStrip from '../../contentQueue/SweepStrip';
import DeleteConfirmationModal from './functions/DeleteConfirmationModal';
import SortHeader, { type SortState } from './functions/SortHeader';
import { sortRows } from './functions/sortRows';

export type { ContentQueueRow } from '../../../lib/keywordSweep';

interface ContentQueueSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

type SortField = 'tier' | 'content_type' | 'title' | 'target_keyword' | 'volume' | 'difficulty' | 'status';

const EMPTY_FORM = {
  title: '',
  recommended_url: '',
  target_keyword: '',
  volume: '',
  difficulty: '',
  notes: '',
  status: DEFAULT_CONTENT_QUEUE_STATUS as string,
  content_type: '',
  tier: '',
  offer_id: '',
};

// Denied and published rows are the queue's past; they stay a click away rather than in the way.
const CLOSED_STATUSES = new Set(['queue_denied', 'published']);

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const oneLine: React.CSSProperties = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
// The table sits inside the section card, so it drops its own card chrome.
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '1040px' };
// Row buttons sit in a dense table row; the sm control is a step too tall for it.
const rowButton: React.CSSProperties = { height: '24px', padding: '0 8px', fontSize: 'var(--text-xs)', gap: '5px' };

/** Keyword difficulty colour: Ahrefs' own bands. */
const kdColor = (kd: number) => (kd <= 30 ? 'var(--status-up)' : kd <= 70 ? 'var(--status-warn)' : 'var(--status-down)');

/** "" → null; anything else must be a whole number within [min, max]. */
const parseCount = (raw: string, min: number, max: number): number | null | 'invalid' => {
  const text = raw.replace(/,/g, '').trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= min && n <= max ? n : 'invalid';
};

/** Hand-added rows from before the sweep have no type; they were all articles. */
const typeKey = (row: ContentQueueRow): ContentType => (contentTypeOf(row.content_type) ? (row.content_type as ContentType) : 'article');

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/* The status badge doubles as the control that changes it — same pattern as the client status menu. */
function StatusPicker({ status, disabled, onChange }: { status: string; disabled?: boolean; onChange: (status: ContentQueueStatus) => void }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const current = contentQueueStatus(status);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        className="pressable"
        title="Change status"
        aria-label={`Status: ${current.label}. Change status`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', opacity: disabled ? 0.6 : 1 }}
      >
        <Badge tone={current.tone} dot>{current.label}</Badge>
      </button>
      <Popover open={open} anchorEl={buttonRef.current} onClose={() => setOpen(false)} offset={6} minWidth={220} role="menu" style={{ display: 'flex', flexDirection: 'column' }}>
        {CONTENT_QUEUE_STATUSES.map((s) => (
          <button
            key={s.value}
            role="menuitemradio"
            aria-checked={s.value === status}
            className="hover-wash pressable"
            onClick={() => {
              setOpen(false);
              if (s.value !== status) onChange(s.value);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '6px 8px',
              border: 'none',
              background: s.value === status ? 'var(--surface-active)' : 'transparent',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Badge tone={s.tone} dot>{s.label}</Badge>
          </button>
        ))}
      </Popover>
    </span>
  );
}

/**
 * Client detail → Content Queue: the pages and articles to make next, in the order to make them.
 *
 * Most rows arrive from the monthly keyword sweep as proposals (A: a landing page for something they
 * sell; B: an article or tool). A person approves or denies each — a deny is a rule the next sweep
 * reads — and hands approved rows on: pages and tools to the build queue as a spec request,
 * articles to the writer. People also add and edit entries by hand; agents read and write the same
 * rows from the command line (scripts/content-queue.py).
 */
export default function ContentQueueSection({ client, expandedSection }: ContentQueueSectionProps) {
  const { user } = useUser();
  const toast = useToast();
  const [rows, setRows] = useState<ContentQueueRow[]>([]);
  const [jobs, setJobs] = useState<Map<string, AiJob>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = queue order (position), the only view rows can be dragged in.
  const [sort, setSort] = useState<SortState<SortField> | null>(null);
  // Filters live in the URL (?tier=A, ?type=money …) so a refresh keeps them; Offers links here with ?type=money.
  const [searchParams, setSearchParams] = useSearchParams();
  const tierFilter = (searchParams.get('tier') ?? 'all') as TierFilter;
  const typeFilter = (searchParams.get('type') ?? 'all') as TypeFilter;
  const setFilterParam = (key: string, value: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete(key);
      else next.set(key, value);
      return next;
    });
  const setTierFilter = (value: TierFilter) => setFilterParam('tier', value);
  const setTypeFilter = (value: TypeFilter) => setFilterParam('type', value);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  // The open entry is ?item=<id>; a new entry stays local.
  const [urlItem, setUrlItem] = useUrlParam('item');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<ContentQueueRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [busyRows, setBusyRows] = useState<Set<string>>(new Set());
  const [sweep, setSweep] = useState<SweepState | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);
  const [enqueueing, setEnqueueing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denying, setDenying] = useState<ContentQueueRow | null>(null);
  const [openJob, setOpenJob] = useState<AiJob | null>(null);

  // A reorder writes one row per moved position, and each write echoes back over realtime. Refetching
  // on those echoes mid-save would paint half-saved orders, so they are ignored until the save ends.
  const savingOrder = useRef(false);
  const fetchSeq = useRef(0);

  const fetchRows = useCallback(
    async (quiet = false) => {
      if (!client?.id) return;
      const seq = ++fetchSeq.current;
      try {
        if (!quiet) setIsLoading(true);
        setError(null);
        const [data, offerRows] = await Promise.all([fetchQueue(client.id), fetchOffers(client.id)]);
        if (seq !== fetchSeq.current) return;
        setRows(data);
        setOffers(offerRows);
        const jobIds = data.map((r) => r.build_job_id).filter((id): id is string => !!id);
        fetchBuildJobs(jobIds)
          .then((map) => {
            if (seq === fetchSeq.current) setJobs(map);
          })
          .catch((err) => console.error('Error loading content queue jobs:', err));
      } catch (err) {
        console.error('Error fetching content queue:', err);
        if (seq === fetchSeq.current) setError('Failed to load the content queue');
      } finally {
        if (seq === fetchSeq.current) setIsLoading(false);
      }
    },
    [client?.id]
  );

  const loadSweep = useCallback(async () => {
    if (!client?.id) return;
    try {
      setSweep(await fetchSweepState(client.id));
      setSweepError(null);
    } catch (err) {
      console.error('Error loading sweep state:', err);
      setSweepError('Couldn’t load the sweep.');
    }
  }, [client?.id]);

  const isOpen = expandedSection === 'content-queue';

  useEffect(() => {
    if (!isOpen) return;
    void fetchRows();
    void loadSweep();
  }, [isOpen, fetchRows, loadSweep]);

  // A sweep lands rows over minutes, and other people approve and deny from their own screens.
  useEffect(() => {
    if (!isOpen || !client?.id) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refetchQueue = () => {
      if (savingOrder.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => void fetchRows(true), 250);
    };
    const filter = `client_id=eq.${client.id}`;
    const channel = supabase
      .channel(uniqueChannelName(`content_queue_${client.id}`))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_content_queue', filter }, refetchQueue)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seo_sweep_runs', filter }, () => void loadSweep())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seo_sweep_rules', filter }, () => void loadSweep())
      .subscribe();
    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [isOpen, client?.id, fetchRows, loadSweep]);

  const passesStatus = useCallback((r: ContentQueueRow) => showClosed || !CLOSED_STATUSES.has(r.status), [showClosed]);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) => passesStatus(r) && (tierFilter === 'all' || r.tier === tierFilter) && (typeFilter === 'all' || (typeFilter === 'money' ? isMoneyPage(r) : typeKey(r) === typeFilter))
      ),
    [rows, passesStatus, tierFilter, typeFilter]
  );
  const displayed = useMemo(() => (sort ? sortRows(visible, sort) : visible), [visible, sort]);

  const filterCounts = useMemo(() => {
    const tiers: Record<Tier, number> = { A: 0, B: 0 };
    const types: Record<ContentType, number> = { landing_page: 0, landing_page_optimise: 0, article: 0, tool: 0 };
    let closed = 0;
    let money = 0;
    for (const r of rows) {
      if (CLOSED_STATUSES.has(r.status)) closed += 1;
      if (!passesStatus(r)) continue;
      if (isMoneyPage(r)) money += 1;
      if (r.tier === 'A' || r.tier === 'B') tiers[r.tier] += 1;
      types[typeKey(r)] += 1;
    }
    return { tiers, types, closed, money };
  }, [rows, passesStatus]);

  const cardTitle = useMemo(() => {
    if (isLoading && rows.length === 0) return 'Loading...';
    if (rows.length === 0) return 'Nothing queued';
    if (visible.length === 0) return 'Nothing showing';
    // Before the sweep every row was an article, and the title said so; keep that until typed rows exist.
    if (!visible.some((r) => r.content_type)) return plural(visible.length, 'article', 'articles');
    const pages = visible.filter((r) => r.content_type === 'landing_page' || r.content_type === 'landing_page_optimise').length;
    const rest = visible.length - pages;
    return [pages ? plural(pages, 'landing page', 'landing pages') : null, rest ? plural(rest, 'article/tool', 'articles/tools') : null]
      .filter(Boolean)
      .join(' · ');
  }, [isLoading, rows.length, visible]);

  /* ───── drag to reorder ───── */

  const reorder = async (activeId: string, overId: string) => {
    // Indices come from the full list, not the filtered view, so rows hidden by a filter keep their
    // place relative to each other.
    const from = rows.findIndex((r) => r.id === activeId);
    const to = rows.findIndex((r) => r.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    const previous = rows;
    const next = arrayMove(rows, from, to);
    setRows(next);
    savingOrder.current = true;
    try {
      await persistOrder(next);
      const index = new Map(next.map((r, i) => [r.id, i]));
      setRows((list) => list.map((r) => (index.has(r.id) ? { ...r, position: index.get(r.id) ?? r.position } : r)));
    } catch (err) {
      console.error('Error saving content queue order:', err);
      setRows(previous);
      toast.error('Couldn’t save the new order', errorText(err, 'The queue is back how it was.'));
      // Some positions may have been written before the failure; show what the database holds.
      void fetchRows(true);
    } finally {
      savingOrder.current = false;
    }
  };

  /* ───── row actions ───── */

  const withRow = async (row: ContentQueueRow, failure: string, work: () => Promise<void>) => {
    if (busyRows.has(row.id)) return;
    setBusyRows((s) => new Set(s).add(row.id));
    try {
      await work();
    } catch (err) {
      console.error(`${failure}:`, err);
      toast.error(failure, errorText(err, 'Nothing was changed.'));
    } finally {
      setBusyRows((s) => {
        const next = new Set(s);
        next.delete(row.id);
        return next;
      });
    }
  };

  const replaceRow = (updated: ContentQueueRow) => setRows((list) => list.map((r) => (r.id === updated.id ? updated : r)));

  const approve = (row: ContentQueueRow) =>
    withRow(row, 'Couldn’t approve it', async () => {
      replaceRow(await approveRow(row));
      toast.success('Approved', row.title);
    });

  const handOff = (row: ContentQueueRow, to: 'build' | 'writer') =>
    withRow(row, to === 'build' ? 'Couldn’t send it to the build queue' : 'Couldn’t send it to the writer', async () => {
      if (!user) throw new Error('Sign in again before queueing work.');
      const input = { row, clientName: client.name, createdBy: { id: user.id, name: user.name } };
      const job = to === 'build' ? await sendToBuildQueue(input) : await sendToWriter(input);
      toast.success(to === 'build' ? 'Sent to the build queue' : 'Sent to the writer', job.title);
      await fetchRows(true);
    });

  const openDeny = (row: ContentQueueRow | null) => {
    setDenying(row);
    setDenyOpen(true);
  };

  const runSweep = async () => {
    if (!user) return toast.error('Sign in again before queueing work');
    setEnqueueing(true);
    try {
      const job = await enqueueSweepJob({ clientId: client.id, clientName: client.name, createdBy: { id: user.id, name: user.name } });
      toast.success('Sweep queued', `${job.title} — proposals land here when it finishes.`);
      await loadSweep();
    } catch (err) {
      console.error('Error queueing sweep:', err);
      toast.error('Couldn’t queue the sweep', errorText(err, 'Nothing was queued.'));
    } finally {
      setEnqueueing(false);
    }
  };

  /* ───── add / edit / delete ───── */

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError(null);
    setIsFormOpen(true);
  };

  const openEdit = (row: ContentQueueRow) => {
    setForm({
      title: row.title,
      recommended_url: row.recommended_url ?? '',
      target_keyword: row.target_keyword ?? '',
      volume: row.volume == null ? '' : String(row.volume),
      difficulty: row.difficulty == null ? '' : String(row.difficulty),
      notes: row.notes ?? '',
      status: row.status,
      content_type: row.content_type ?? '',
      tier: row.tier ?? '',
      offer_id: row.offer_id ?? '',
    });
    setEditingId(row.id);
    setFormError(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setFormError(null);
    if (urlItem) setUrlItem(null);
  };

  const handleSubmit = async () => {
    const volume = parseCount(form.volume, 0, 2_000_000_000);
    const difficulty = parseCount(form.difficulty, 0, 100);
    if (!form.title.trim()) return setFormError('A title is required');
    if (volume === 'invalid') return setFormError('VOL must be a whole number');
    if (difficulty === 'invalid') return setFormError('KD must be a whole number from 0 to 100');

    const entry = {
      title: form.title.trim(),
      recommended_url: form.recommended_url.trim() || null,
      target_keyword: form.target_keyword.trim() || null,
      volume,
      difficulty,
      notes: form.notes.trim() || null,
      status: form.status,
      content_type: form.content_type || null,
      tier: form.tier || null,
      offer_id: form.offer_id || null,
    };

    setIsSubmitting(true);
    setFormError(null);
    try {
      if (editingId) {
        const { error: updateError } = await supabase.from('client_content_queue').update(entry).eq('id', editingId);
        if (updateError) throw updateError;
      } else {
        // A hand-added row joins the bottom of the queue; people drag it up from there.
        const position = await nextPosition(client.id);
        const { error: insertError } = await supabase
          .from('client_content_queue')
          .insert({ ...entry, position, client_id: client.id, source: 'manual', created_by: user?.id ?? null });
        if (insertError) throw insertError;
      }
      closeForm();
      await fetchRows(true);
    } catch (err) {
      console.error('Error saving content queue entry:', err);
      setFormError('Failed to save the entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeStatus = async (row: ContentQueueRow, next: ContentQueueStatus) => {
    const apply = (status: string) => setRows((list) => list.map((r) => (r.id === row.id ? { ...r, status } : r)));
    apply(next);
    setSavingStatus(row.id);
    const { error: updateError } = await supabase.from('client_content_queue').update({ status: next }).eq('id', row.id);
    if (updateError) {
      console.error('Error updating content queue status:', updateError);
      apply(row.status);
      toast.error('Couldn’t change the status', 'The change was not saved.');
    }
    setSavingStatus(null);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      setIsDeleting(true);
      setError(null);
      const { error: deleteError } = await supabase.from('client_content_queue').delete().eq('id', deleting.id);
      if (deleteError) throw deleteError;
      setDeleting(null);
      await fetchRows(true);
    } catch (err) {
      console.error('Error deleting content queue entry:', err);
      setError('Failed to remove the entry.');
      setDeleting(null);
    } finally {
      setIsDeleting(false);
    }
  };

  useUrlPanelSync(
    urlItem,
    isFormOpen ? editingId : null,
    !isLoading && rows.length > 0,
    (id) => {
      const row = rows.find((r) => r.id === id);
      if (row) openEdit(row);
    },
    () => {
      setIsFormOpen(false);
      setEditingId(null);
      setFormError(null);
    },
  );

  if (!isOpen) return null;


  const header = (label: string, field: SortField, title?: string) => (
    // SortHeader needs a state to compare against; in queue order no column is active.
    <SortHeader label={label} field={field} sort={sort ?? { field: '' as SortField, dir: 'desc' }} onSort={setSort} title={title} />
  );

  const rowActions = (row: ContentQueueRow) => {
    const busy = busyRows.has(row.id);
    const job = row.build_job_id ? jobs.get(row.build_job_id) : null;
    const buttons: React.ReactNode[] = [];

    if (row.status === 'queue_submitted') {
      buttons.push(
        <Button key="approve" size="sm" variant="secondary" icon="check" onClick={() => approve(row)} disabled={busy} style={rowButton}>
          Approve
        </Button>,
        <Button key="deny" size="sm" variant="ghost" icon="x" onClick={() => openDeny(row)} disabled={busy} style={rowButton}>
          Deny
        </Button>
      );
    } else if (row.status === 'queue_approved' && !row.build_job_id) {
      if (goesToBuildQueue(row.content_type)) {
        buttons.push(
          <Button key="build" size="sm" variant="secondary" icon="hammer" onClick={() => handOff(row, 'build')} disabled={busy} style={rowButton} title="Queue a spec for this page">
            Send to build queue
          </Button>
        );
      } else if (!row.content_type || row.content_type === 'article') {
        buttons.push(
          <Button key="writer" size="sm" variant="secondary" icon="send" onClick={() => handOff(row, 'writer')} disabled={busy} style={rowButton} title="Queue this article for the content writer">
            Send to writer
          </Button>
        );
      }
    }

    if (row.build_job_id) {
      buttons.push(
        job ? (
          <button
            key="job"
            type="button"
            className="pressable"
            title={aiJobChipLabel(job)}
            onClick={() => setOpenJob(job)}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
          >
            <Badge tone={aiJobStatusTone(job.status)} dot>
              {AI_JOB_STATUS_LABEL[job.status] ?? job.status}
            </Badge>
          </button>
        ) : (
          <span key="job" style={{ ...faint, fontSize: 'var(--text-xs)' }}>
            job…
          </span>
        )
      );
    }

    return (
      <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', width: '100%' }}>
        {buttons}
        <KebabMenu
          label="Entry actions"
          actions={[
            { label: 'Edit Entry', icon: faPen, onClick: () => setUrlItem(row.id) },
            { label: 'Delete', icon: faTrash, danger: true, onClick: () => setDeleting(row) },
          ]}
        />
      </span>
    );
  };

  const columns: TableColumn<ContentQueueRow>[] = [
    {
      key: 'tier',
      label: header('Tier', 'tier'),
      width: '6%',
      render: (_v, row) => {
        const tier = tierOf(row.tier);
        if (!tier) return <span style={faint}>—</span>;
        return (
          <Badge tone={tier.tone} title={briefOf(row).tier_reason || tier.blurb}>
            {tier.label}
          </Badge>
        );
      },
    },
    {
      key: 'content_type',
      label: header('Type', 'content_type'),
      width: '9%',
      render: (_v, row) => {
        const type = contentTypeOf(row.content_type);
        if (!type) return <span style={faint}>—</span>;
        return (
          <Badge tone={type.tone} title={type.label}>
            {type.short}
          </Badge>
        );
      },
    },
    {
      key: 'title',
      label: header('Title', 'title'),
      width: '25%',
      render: (_v, row) => {
        const pitch = briefOf(row).pitch;
        const tooltip = [row.title, row.recommended_url, pitch, row.notes].filter(Boolean).join('\n\n');
        return (
          <span style={{ display: 'block', minWidth: 0 }} title={tooltip}>
            <span style={{ ...oneLine, fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>{row.title}</span>
            {row.offer_id ? (
              <span style={{ ...oneLine, color: 'var(--accent-text)', fontSize: 'var(--text-2xs)', marginTop: '1px' }}>Offer: {offerName.get(row.offer_id) ?? 'removed'}</span>
            ) : null}
            {pitch ? <span style={{ ...oneLine, ...faint, fontSize: 'var(--text-xs)', marginTop: '1px' }}>{pitch}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'target_keyword',
      label: header('Keyword', 'target_keyword'),
      width: '14%',
      render: (_v, row) => {
        const keyword = row.target_keyword ?? briefOf(row).keyword;
        return keyword ? <span style={oneLine} title={keyword}>{keyword}</span> : <span style={faint}>—</span>;
      },
    },
    {
      key: 'volume',
      label: header('VOL', 'volume', 'Monthly search volume for the target keyword'),
      width: '6%',
      align: 'right',
      mono: true,
      render: (_v, row) => {
        const volume = row.volume ?? briefOf(row).volume;
        return volume == null ? <span style={faint}>—</span> : volume.toLocaleString('en-US');
      },
    },
    {
      key: 'difficulty',
      label: header('KD', 'difficulty', 'Keyword difficulty, 0–100 — against the cap the sweep allowed for this site'),
      width: '6%',
      align: 'right',
      mono: true,
      render: (_v, row) => {
        const brief = briefOf(row);
        const kd = row.difficulty ?? brief.kd;
        if (kd == null) return <span style={faint}>—</span>;
        return (
          <span title={brief.kd_cap != null ? `KD ${kd}, cap ${brief.kd_cap}${brief.dr != null ? ` (DR ${brief.dr})` : ''}` : undefined}>
            <span style={{ color: kdColor(kd), fontWeight: 600 }}>{kd}</span>
            {brief.kd_cap != null ? <span style={faint}>/{brief.kd_cap}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: header('Status', 'status'),
      width: '13%',
      render: (_v, row) => <StatusPicker status={row.status} disabled={savingStatus === row.id || busyRows.has(row.id)} onChange={(next) => changeStatus(row, next)} />,
    },
    {
      key: 'id',
      label: '',
      width: '21%',
      align: 'right',
      render: (_v, row) => rowActions(row),
    },
  ];

  const offerName = new Map(offers.map((o) => [o.id, o.name]));
  // Production offers can take money pages; a row already tied to another offer keeps it as an option.
  const offerOptions = [
    { value: '', label: 'No offer' },
    ...offers.filter((o) => o.status === 'production' || o.id === form.offer_id).map((o) => ({ value: o.id, label: o.status === 'production' ? o.name : `${o.name} (${o.status})` })),
  ];

  const field = (key: 'title' | 'recommended_url' | 'target_keyword' | 'volume' | 'difficulty') => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const emptyCopy = (text: React.ReactNode) => (
    <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>{text}</div>
  );

  return (
    <Card
      eyebrow="Content Queue"
      title={cardTitle}
      actions={
        <Button size="sm" variant="secondary" icon="plus" onClick={openAdd}>
          Add Entry
        </Button>
      }
    >
      <SweepStrip
        clientName={client.name}
        state={sweep}
        error={sweepError}
        enqueueing={enqueueing}
        onRunNow={runSweep}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenRules={() => openDeny(null)}
      />

      {error ? <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>{error}</div> : null}

      {rows.length === 0 ? (
        isLoading ? null : (
          emptyCopy(<>The pages and articles to make next for {client.name}: a title, where it should live, and the keyword it goes after.</>)
        )
      ) : (
        <>
          <QueueFilters
            tier={tierFilter}
            onTier={setTierFilter}
            type={typeFilter}
            onType={setTypeFilter}
            tierCounts={filterCounts.tiers}
            typeCounts={filterCounts.types}
            moneyCount={filterCounts.money}
            showClosed={showClosed}
            onShowClosed={setShowClosed}
            closedCount={filterCounts.closed}
            sorted={sort != null}
            onQueueOrder={() => setSort(null)}
          />
          {displayed.length === 0 ? (
            emptyCopy('Nothing in the queue matches these filters.')
          ) : (
            <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
              <QueueTable columns={columns} rows={displayed} sortable={sort == null} onReorder={reorder} onRowClick={(row) => setUrlItem(row.id)} style={flatTable} />
            </div>
          )}
        </>
      )}

      <SidePanel
        open={isFormOpen}
        onClose={closeForm}
        width={460}
        eyebrow={`Content Queue · ${client.name}`}
        title={editingId ? 'Edit Entry' : 'Add Entry'}
        footer={
          <>
            <Button icon="check" onClick={handleSubmit} disabled={isSubmitting || !form.title.trim()}>
              {isSubmitting ? 'Saving...' : editingId ? 'Update Entry' : 'Add Entry'}
            </Button>
            <Button variant="ghost" onClick={closeForm} disabled={isSubmitting}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formError ? (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {formError}
            </div>
          ) : null}

          <Input label="Title" placeholder="How to choose a…" {...field('title')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Dropdown
              label="Type"
              value={form.content_type}
              onChange={(e) => setForm((f) => ({ ...f, content_type: e.target.value }))}
              options={[{ value: '', label: 'No type' }, ...(Object.keys(CONTENT_TYPES) as ContentType[]).map((t) => ({ value: t, label: CONTENT_TYPES[t].label }))]}
              style={{ width: '100%' }}
            />
            <Dropdown
              label="Tier"
              value={form.tier}
              onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
              options={[{ value: '', label: 'No tier' }, ...(Object.keys(TIERS) as Tier[]).map((t) => ({ value: t, label: `${t} — ${t === 'A' ? 'landing page' : 'article or tool'}` }))]}
              style={{ width: '100%' }}
            />
          </div>
          {offerOptions.length > 1 ? (
            <Dropdown
              label="Offer"
              value={form.offer_id}
              onChange={(e) => setForm((f) => ({ ...f, offer_id: e.target.value }))}
              options={offerOptions}
              style={{ width: '100%' }}
            />
          ) : null}
          <Input label="Recommended URL" placeholder="/blog/how-to-choose-a…/" {...field('recommended_url')} />
          <Input label="Main Target Keyword" placeholder="how to choose a…" {...field('target_keyword')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input label="VOL" inputMode="numeric" placeholder="Monthly searches" {...field('volume')} />
            <Input label="KD" inputMode="numeric" placeholder="0–100" {...field('difficulty')} />
          </div>
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Status</div>
            <Dropdown
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              options={CONTENT_QUEUE_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Angle, internal links to include, the page it supports..."
              className="ck-textarea"
            />
          </div>
        </div>
      </SidePanel>

      <DenyPanel
        open={denyOpen}
        row={denying}
        clientName={client.name}
        rules={sweep?.rules ?? []}
        userId={user?.id ?? null}
        onClose={() => setDenyOpen(false)}
        onDenied={replaceRow}
        onRulesChanged={loadSweep}
      />

      <SweepSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        clientId={client.id}
        clientName={client.name}
        settings={sweep?.settings ?? null}
        onSaved={loadSweep}
      />

      {/* The job a row was handed to opens in the same operator panel Fulfillment uses. */}
      {openJob ? (
        <AiJobChips
          key={openJob.id}
          job={openJob}
          panelOnly
          initialOpenJobId={openJob.id}
          onClosePanel={() => {
            setOpenJob(null);
            void fetchRows(true);
          }}
        />
      ) : null}

      <DeleteConfirmationModal
        isOpen={deleting != null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Remove from the queue?"
        message={deleting ? `“${deleting.title}” will be removed from ${client.name}’s content queue.` : undefined}
        isDeleting={isDeleting}
      />
    </Card>
  );
}
